import Dexie, { type EntityTable } from 'dexie';
import { createId } from './lib/id';
import { repairedLegacyMetadata } from './lib/places';
import type { AppSetting, ItemRecord, OutboxEvent } from './types';

class HouseholdDatabase extends Dexie {
  items!: EntityTable<ItemRecord, 'id'>;
  outbox!: EntityTable<OutboxEvent, 'itemId'>;
  settings!: EntityTable<AppSetting, 'key'>;

  constructor() {
    super('household-items-pwa');
    this.version(1).stores({
      items: 'id, status, createdAt, updatedAt, pendingSync, locationGroup',
      outbox: 'itemId, id, type, createdAt',
      settings: 'key'
    });
    this.version(2).stores({
      items: 'id, status, createdAt, updatedAt, pendingSync, locationGroup',
      outbox: 'itemId, id, type, createdAt',
      settings: 'key'
    }).upgrade(async (transaction) => {
      await transaction.table<ItemRecord>('items').toCollection().modify((item) => {
        item.name = safeString(item.name);
        item.locationNote = safeString(item.locationNote);
        item.locationGroup = safeString(item.locationGroup);
        item.photoMimeType = safeString(item.photoMimeType) || 'image/jpeg';
        item.synonyms = safeStringArray(item.synonyms);
        item.aiSuggestions = safeStringArray(item.aiSuggestions);
        item.pendingSync = Boolean(item.pendingSync);
      });
    });
    this.version(3).stores({
      items: 'id, status, createdAt, updatedAt, pendingSync, locationGroup',
      outbox: 'itemId, id, type, createdAt',
      settings: 'key'
    }).upgrade(async (transaction) => {
      const items = transaction.table<ItemRecord>('items');
      const outbox = transaction.table<OutboxEvent>('outbox');
      const records = await items.toArray();
      const updatedAt = new Date().toISOString();

      for (const item of records) {
        const repair = repairedLegacyMetadata(item);
        if (!repair) continue;

        const updated: ItemRecord = { ...item, ...repair, updatedAt, pendingSync: true };
        const { photo: _photo, ...payload } = updated;
        await items.put(updated);
        await outbox.put({
          itemId: item.id,
          id: createId('event'),
          type: 'item.upsert',
          payload,
          createdAt: updatedAt
        });
      }
    });
  }
}

export const db = new HouseholdDatabase();

function safeString(value: unknown) {
  return typeof value === 'string' ? value : '';
}

function safeStringArray(value: unknown) {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}
